import { DestroyRef, Injectable } from '@angular/core';
import { get } from 'lodash';
import { LocalStorageService } from 'ngx-webstorage';

import { Hospital } from '@shared/models/hospital.model';
import { InstanceIdService } from '@shared/services/instance-id.service';
import { SubscriptionManager } from '@shared/services/subscription-manager.service';
import { Subject, Subscription } from 'rxjs';
import { BufferProgram } from 'src/app/shared/models/buffer-program.model';
import { User } from '../../shared/models/user.model';
import { Token } from '../models/token.model';

const tokenStorageKey: string = 'SOP_USER_TOKEN';
const userStorageKey: string = 'SOP_USER_OBJECT';

@Injectable({
  providedIn: 'root',
})
export class StorageService {
  private instanceId: string;

  // IDS des rooms on l'on va sauvegarder les programmes dans le cache (a la suppression d'une vacation)
  public programsToSaveInCache: BufferProgram[] = [];

  // this subject will listen the change on profile picture in 'my-account' in order to
  // propagate it in the sidenav
  public profilePictureInLocalStorageSubject: Subject<string> = new Subject<string>();

  //share logo updates by hospitalName
  public hospitalGroupLogoSubject = new Subject<{ hospitalName: string; logoUrl: string }>();

  //manage a version for cache-busting so that browsers reload the latest logo instead of showing a cached one
  setHospitalLogoVersion(hospitalName: string, version: number): void {
    localStorage.setItem(`logoVer_${hospitalName}`, String(version));
  }

  getHospitalLogoVersion(hospitalName: string): number {
    const v = localStorage.getItem(`logoVer_${hospitalName}`);
    return v ? Number(v) : 0;
  }

  // build the url logo with cache busting
  buildBustedLogoUrl(hospitalName: string, logoUrl?: string): string {
    const ver = this.getHospitalLogoVersion(hospitalName);
    return logoUrl ? `${logoUrl}?v=${ver}` : '';
  }

  //update the user's hospital logo in local storage with the busted one
  updateUserHospitalLogo(hospitalName: string, bustedUrl: string): void {
    const u = this.getUser();
    if (u?.profile?.hospitals) {
      u.profile.hospitals = u.profile.hospitals.map((h: Hospital) =>
        String(h.hospitalName) === String(hospitalName) ? { ...h, logoUrl: bustedUrl } : h
      );
      this.storeUser(u);
    }
  }

  //subscribe to hospital logo changes and return busted URLs
  onHospitalLogoChange(cb: (hospitalName: string, bustedUrl: string) => void): Subscription {
    return this.subscriptionManager
      .createRequest(this.hospitalGroupLogoSubject, 'hospitalGroupLogoSubject', this.instanceId)
      .subscribe({
        next: ({ hospitalName, logoUrl }: { hospitalName: string; logoUrl: string }) => {
          const busted = this.buildBustedLogoUrl(hospitalName, logoUrl);
          cb(hospitalName, busted);
        },
        error: (err: any) => {
          console.error('Error in hospital group logo subscription:', err);
        },
      });
  }

  constructor(
    private localStorageService: LocalStorageService,
    private destroyRef: DestroyRef,
    private subscriptionManager: SubscriptionManager,
    private instanceIdService: InstanceIdService
  ) {
    this.instanceId = this.instanceIdService.generateId();
    this.subscriptionManager.registerComponent(this.instanceId, this.destroyRef);
  }

  getAccessToken(): string | null {
    const token: Token = this.getToken();
    return get(token, 'access_token', null);
  }

  getToken(): Token {
    return this.localStorageService.retrieve(tokenStorageKey);
  }

  storeToken(token: Token): void {
    this.localStorageService.store(tokenStorageKey, token);
  }

  storeUser(user: User): void {
    this.localStorageService.store(userStorageKey, user);
  }

  getUser(): User {
    return this.localStorageService.retrieve(userStorageKey);
  }

  clearUser(): void {
    this.localStorageService.clear(userStorageKey);
  }

  saveData(key: string, data: any) {
    localStorage.setItem(key, JSON.stringify(data));
  }

  getData(key: string) {
    const data = localStorage.getItem(key);
    if (data) {
      return JSON.parse(data);
    }
    return null;
  }

  clearAllLocalStorageKeys(): void {
    localStorage.clear();
    this.localStorageService.clear();
  }

  clear() {
    this.programsToSaveInCache = [];

    this.profilePictureInLocalStorageSubject.complete();
    this.profilePictureInLocalStorageSubject = new Subject<string>();

    this.hospitalGroupLogoSubject.complete();
    this.hospitalGroupLogoSubject = new Subject<{ hospitalName: string; logoUrl: string }>();
  }
}
